import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Paperclip, X, File, Image, FileText, Upload, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const ALLOWED_TYPES = {
  'application/pdf': { icon: FileText, label: 'PDF' },
  'application/msword': { icon: FileText, label: 'DOC' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, label: 'DOCX' },
  'application/vnd.ms-excel': { icon: FileText, label: 'XLS' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileText, label: 'XLSX' },
  'image/png': { icon: Image, label: 'PNG' },
  'image/jpeg': { icon: Image, label: 'JPG' },
  'image/gif': { icon: Image, label: 'GIF' },
  'text/plain': { icon: File, label: 'TXT' },
};

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

export default function FileUploader({ onFileUploaded, disabled }) {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Fichier trop volumineux (max 25 MB)');
      return;
    }

    // Check file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt'];
    if (!allowedExtensions.includes(ext)) {
      toast.error(`Format non supporté. Formats acceptés: ${allowedExtensions.join(', ')}`);
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('client_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('file_type', 'document');

      const response = await fetch(`${API_URL}/api/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        if (onFileUploaded) {
          onFileUploaded({
            file_url: data.file_url,
            file_name: data.file_name,
            file_type: selectedFile.type.startsWith('image/') ? 'image' : 'file',
            original_name: selectedFile.name,
            size: data.size
          });
        }
        
        // Reset state
        clearSelection();
        toast.success('Fichier envoyé');
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error("Erreur lors de l'envoi du fichier");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType) => {
    const typeInfo = ALLOWED_TYPES[mimeType];
    return typeInfo?.icon || File;
  };

  // File selected, show preview
  if (selectedFile) {
    const FileIcon = getFileIcon(selectedFile.type);
    const isImage = selectedFile.type.startsWith('image/');

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute bottom-full left-0 right-0 mb-2 p-3 bg-white rounded-xl shadow-lg border border-slate-200"
      >
        <div className="flex items-start gap-3">
          {/* Preview */}
          <div className="w-16 h-16 flex-shrink-0 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
            {isImage && previewUrl ? (
              <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <FileIcon className="w-8 h-8 text-slate-400" />
            )}
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-rm-text truncate">{selectedFile.name}</p>
            <p className="text-xs text-rm-text-secondary mt-0.5">
              {formatFileSize(selectedFile.size)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
              disabled={isUploading}
            >
              <X className="w-4 h-4" />
            </button>
            <Button
              onClick={handleUpload}
              disabled={isUploading}
              size="sm"
              className="bg-rm-primary hover:bg-rm-primary/90"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Upload progress */}
        {isUploading && (
          <div className="mt-2">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-rm-primary"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2 }}
              />
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // Initial state - upload button
  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
        title="Joindre un fichier"
      >
        <Paperclip className="w-5 h-5" />
      </button>
    </>
  );
}
